import {
  getBackendLanguage,
  hasAnyTranslation,
  translateBackend,
} from '@assets/i18n/config';
import type { TargetAllocation } from '@interfaces/financialProduct';
import { Strategy } from '@interfaces/financialProduct';
import type { TranslatedString } from '@interfaces/shared';
import { LanguageCodes } from '@onevesthq/ov-enums';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { useTextFieldTokens } from '@ovComponents/2-component/textField/textField.tokens';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import TranslatableRichTextEditor from '@ovComponents/3-pattern/translatableRichTextEditor/translatableRichTextEditor';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Button, Grid, Typography, useTheme } from 'hephaestus/web';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio.types';
import {
  DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  MODEL_PORTFOLIO_GROUP_BY,
  type ModelPortfolioGroupBy,
} from '../createModelPortfolio/modelPortfolioGroupBy';
import type { AddModelPortfoliosIPSAndAssetClassesOptions } from './addModelPortfoliosIPSAndAssetClasses';
import type { AssetClassTargetAllocation } from './assetClassTargetAllocation.types';
import { hasUnusableAllocation } from './assetClassTargetAllocation.utils';
import { AssetClassesTargetAllocations } from './components/assetClassesTargetAllocations';

export type { AssetClassTargetAllocation } from './assetClassTargetAllocation.types';

export const AddModelPortfoliosIPSAndAssetClassesVisual = ({
  options,
  workflowCompletion,
  stepLoading,
  modelPortfolioData,
  setModelPortfolioData,
  onSaveForLater,
  onCreateModelPortfolio,
  loading,
}: {
  options: AddModelPortfoliosIPSAndAssetClassesOptions;
  workflowCompletion: any;
  stepLoading: boolean;
  modelPortfolioData: ModelPortfolioData;
  setModelPortfolioData: (data: ModelPortfolioData) => void;
  onSaveForLater: () => void;
  onCreateModelPortfolio: () => void | Promise<void>;
  loading: boolean;
}) => {
  const { t } = useTranslation([
    'modelPortfolios',
    'shared',
    'components',
    'client',
  ]);
  const [focused, setFocused] = useState<string[]>([]);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [translatedPortfolioDescription, setTranslatedPortfolioDescription] =
    useState<TranslatedString>(
      modelPortfolioData?.translatedPortfolioDescription ||
        getLanguageDefaults(),
    );
  const [translatedInvestmentObjective, setTranslatedInvestmentObjective] =
    useState<TranslatedString>(
      modelPortfolioData?.translatedInvestmentObjective ||
        getLanguageDefaults(),
    );
  const [translatedLiquidity, setTranslatedLiquidity] =
    useState<TranslatedString>(
      modelPortfolioData?.translatedLiquidity || getLanguageDefaults(),
    );
  const [translatedPortfolioDisclaimer, setTranslatedPortfolioDisclaimer] =
    useState<TranslatedString>(
      modelPortfolioData?.translatedPortfolioDisclaimer ||
        getLanguageDefaults(),
    );
  const [targetAllocations, setTargetAllocations] = useState<
    AssetClassTargetAllocation[]
  >(modelPortfolioData.targetAllocations || []);
  const [groupBy, setGroupBy] = useState<ModelPortfolioGroupBy>(
    modelPortfolioData.groupBy ?? DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  );
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);

  const handleGroupByChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const newGroupBy = e.target.value as ModelPortfolioGroupBy;
    setGroupBy(newGroupBy);

    const updatedAllocations = targetAllocations.map((allocation) => {
      if (allocation.fromSecurity) {
        return {
          ...allocation,
          secondaryAssetClass: {
            id:
              newGroupBy === MODEL_PORTFOLIO_GROUP_BY.PRIMARY
                ? allocation.securityPrimaryAssetClassId
                : allocation.securitySecondaryAssetClassId,
          },
        };
      }
      return allocation;
    });

    setTargetAllocations(updatedAllocations);
    update('targetAllocations', updatedAllocations);
    update('groupBy', newGroupBy);
  };

  const update = useCallback(
    (field: string, value: string | TranslatedString | TargetAllocation[]) => {
      if (
        typeof value === 'object' &&
        Object.values(LanguageCodes).some((code) => code in value)
      ) {
        const translatedValue = value as TranslatedString;
        if (!hasAnyTranslation(translatedValue)) {
          return;
        }
      }

      const updatedData = {
        ...modelPortfolioData,
        [field]: value,
      };

      setModelPortfolioData(updatedData);
    },
    [modelPortfolioData, setModelPortfolioData],
  );

  const handleTargetAllocationsChange = (
    newTargetAllocations: AssetClassTargetAllocation[],
  ) => {
    setTargetAllocations(newTargetAllocations);
    update('targetAllocations', newTargetAllocations);
  };

  const validateField = (
    enabled: boolean,
    required: string,
    value: any,
  ): boolean => enabled && required === FieldRequiredType.ERROR && !value;

  const validateTranslatedField = (
    enabled: boolean,
    required: string,
    value: TranslatedString | undefined,
  ): boolean =>
    enabled &&
    required === FieldRequiredType.ERROR &&
    !value?.[getBackendLanguage()];

  const validateFields = (returnFields = false) => {
    const fields: string[] = [];

    if (options.ips?.enabled) {
      if (
        validateTranslatedField(
          options.ips.options.description.enabled,
          options.ips.options.description.required,
          translatedPortfolioDescription,
        )
      ) {
        fields.push('description');
      }

      if (
        validateTranslatedField(
          options.ips.options.objective.enabled,
          options.ips.options.objective.required,
          translatedInvestmentObjective,
        )
      ) {
        fields.push('objective');
      }

      if (
        validateTranslatedField(
          options.ips.options.liquidity.enabled,
          options.ips.options.liquidity.required,
          translatedLiquidity,
        )
      ) {
        fields.push('liquidity');
      }
    }

    if (
      validateField(
        options.disclaimer?.enabled,
        options.disclaimer?.required,
        translatedPortfolioDisclaimer,
      )
    ) {
      fields.push('disclaimer');
    }

    if (
      validateField(
        options.assetClassesDistribution?.enabled,
        options.assetClassesDistribution?.required,
        targetAllocations.length,
      )
    ) {
      fields.push('assetClassesDistribution');
    }

    if (returnFields) {
      setFocused(fields);
      return fields.length === 0;
    }
    return fields.length === 0;
  };

  const isDataValid = () => validateFields(false);

  const validate = () => validateFields(true);

  const isValidTargetAllocations = useMemo(
    () => !hasUnusableAllocation(targetAllocations),
    [targetAllocations],
  );

  const submit = async () => {
    if (validate()) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsAddModelPortfolioDetailsContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );
      sendAnalytic(ovAnalyticsEvents.modelPageCreateStep4AddIpsDone, {
        targetAllocationsCount: targetAllocations.length,
        timestamp: new Date().toISOString(),
      });
      await onCreateModelPortfolio();
    }
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('shared:done'),
        disabled: stepLoading || !isDataValid() || !isValidTargetAllocations,
        dataTestId: 'add-model-portfolio-ips-continue-button',
      }}
      buttonFooter={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'flex-end',
          }}
        >
          {options?.buttonSaveForLater?.enabled && (
            <Button
              data-testid='add-model-portfolio-ips-save-for-later-button'
              variant='contained'
              disabled={
                stepLoading || !isDataValid() || !isValidTargetAllocations
              }
              sx={{ mr: 2, textAlign: 'center' }}
              onClick={() => {
                sendAnalytic(
                  ovAnalyticsEvents.modelPageCreateStep4AddIpsSaveLater,
                  {
                    targetAllocationsCount: targetAllocations.length,
                    timestamp: new Date().toISOString(),
                  },
                );
                onSaveForLater();
              }}
              loading={loading}
              color='secondary'
            >
              {translateBackend(options?.buttonSaveForLater?.label)}
            </Button>
          )}
        </Box>
      }
    >
      {options?.title && (
        <Typography
          variant='displayLarge'
          data-testid='add-model-portfolio-ips-title'
          sx={{
            mt: 1,
            mb: 2,
            fontWeight: 400,
            fontSize: '40px',
            fontFamily: 'Tiempos Text',
          }}
        >
          {translateBackend(options?.title)}
        </Typography>
      )}

      <Grid container spacing={2}>
        {options?.ips?.options?.description?.enabled && (
          <Grid size={12}>
            <TranslatableTextField
              testId='ips-description'
              onChange={(value) => {
                setTranslatedPortfolioDescription(value);
                update('translatedPortfolioDescription', value);
                setFocused([...focused, 'description']);
              }}
              disabled={stepLoading}
              label={translateBackend(
                options?.ips?.options?.description?.label,
              )}
              fullWidth
              multiline
              rows={1}
              value={translatedPortfolioDescription}
              onBlur={() => setFocused([...focused, 'description'])}
              error={
                !translatedPortfolioDescription[getBackendLanguage()] &&
                focused.includes('description')
              }
              required={
                options.ips.options.description.required ===
                FieldRequiredType.ERROR
              }
            />
          </Grid>
        )}

        {options?.ips?.options?.objective?.enabled && (
          <Grid size={12}>
            <TranslatableTextField
              testId='ips-objective'
              onChange={(value) => {
                setTranslatedInvestmentObjective(value);
                update('translatedInvestmentObjective', value);
                setFocused([...focused, 'objective']);
              }}
              disabled={stepLoading}
              label={translateBackend(options?.ips?.options?.objective?.label)}
              fullWidth
              multiline
              rows={1}
              value={translatedInvestmentObjective}
              onBlur={() => setFocused([...focused, 'objective'])}
              error={
                !translatedInvestmentObjective[getBackendLanguage()] &&
                focused.includes('objective')
              }
              required={
                options.ips.options.objective.required ===
                FieldRequiredType.ERROR
              }
            />
          </Grid>
        )}

        {options?.ips?.options?.liquidity?.enabled && (
          <Grid size={12}>
            <TranslatableTextField
              testId='ips-liquidity'
              onChange={(value) => {
                setTranslatedLiquidity(value);
                update('translatedLiquidity', value);
                setFocused([...focused, 'liquidity']);
              }}
              disabled={stepLoading}
              label={translateBackend(options?.ips?.options?.liquidity?.label)}
              fullWidth
              multiline
              rows={1}
              value={translatedLiquidity}
              onBlur={() => setFocused([...focused, 'liquidity'])}
              required={
                options.ips.options.liquidity.required ===
                FieldRequiredType.ERROR
              }
            />
          </Grid>
        )}

        {options?.disclaimer?.enabled && (
          <Grid size={12}>
            <TranslatableRichTextEditor
              testId='ips-disclaimer-workflow-substep'
              value={translatedPortfolioDisclaimer}
              setValue={(value) => {
                setTranslatedPortfolioDisclaimer(value);
                update('translatedPortfolioDisclaimer', value);
                setFocused([...focused, 'disclaimer']);
              }}
              label={translateBackend(options?.disclaimer?.label)}
              fullToolbar={false}
            />
          </Grid>
        )}

        {options?.assetClassesDistribution?.enabled && (
          <Grid size={12}>
            {modelPortfolioData?.strategy === Strategy.ASSET_CLASS && (
              <Typography
                variant='bodySmall'
                component='div'
                sx={{ mb: 2, display: 'block' }}
              >
                {t('modelPortfolios:assetClassIPSDisclaimer')}
              </Typography>
            )}
            <Box sx={{ mb: 2 }}>
              <SelectField
                testId='group-by-select'
                label={t('client:groupBy')}
                value={groupBy}
                onChange={handleGroupByChange}
                fullWidth
              >
                <MenuItem
                  data-testid='group-by-select-primary'
                  value={MODEL_PORTFOLIO_GROUP_BY.PRIMARY}
                >
                  {t('components:productShelf.exportHeaders.primaryAssetClass')}
                </MenuItem>
                <MenuItem
                  data-testid='group-by-select-secondary'
                  value={MODEL_PORTFOLIO_GROUP_BY.SECONDARY}
                >
                  {t(
                    'components:productShelf.exportHeaders.secondaryAssetClass',
                  )}
                </MenuItem>
              </SelectField>
            </Box>
            <Typography
              data-testid='ips-asset-classes-distribution-label'
              variant='labelSmall'
              component='div'
              sx={{ mb: '4px', color: tokens.labelColor }}
            >
              {translateBackend(options?.assetClassesDistribution?.label)}
              {options.assetClassesDistribution.required ===
                FieldRequiredType.ERROR && ' *'}
            </Typography>
            <AssetClassesTargetAllocations
              testId='ips-asset-classes-distribution'
              targetAllocations={targetAllocations}
              setTargetAllocations={handleTargetAllocationsChange}
              securities={modelPortfolioData?.securities}
              groupBy={groupBy}
              strategy={modelPortfolioData?.strategy}
              assetClassBranches={modelPortfolioData?.assetClassBranches}
            />
          </Grid>
        )}
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default AddModelPortfoliosIPSAndAssetClassesVisual;
